/**
 * Turn a partial intake draft (from PDF fusion or chat extraction) into a
 * fully-formed ProjectCreateInput, or a validation error. Pure + shared by
 * the PDF and chat intake pages so the "review → create" contract is
 * identical no matter how the draft was produced (D.1).
 */
import type { ProjectCreateInput } from '@vari-crew/schemas';

export type DraftToCreateResult =
  | { ok: true; value: ProjectCreateInput }
  | { ok: false; error: string };

export function draftToCreateInput(
  draft: Partial<ProjectCreateInput> | null | undefined,
  fallbackSource: ProjectCreateInput['source'] = 'form',
): DraftToCreateResult {
  if (!draft) return { ok: false, error: 'No draft to create from.' };

  const title = draft.title?.trim();
  const client = draft.client_name?.trim();
  if (!title) return { ok: false, error: 'Project title is required.' };
  if (!client) return { ok: false, error: 'Client name is required.' };

  // Required at intake (VCS-201/202): a concrete start (day + time) and a hard
  // deadline. The review form gates submit on these, but enforce here too so the
  // shared builder can't produce an invalid request.
  const requestedStart = draft.requested_start_date;
  const deadline = draft.deadline_date;
  if (!requestedStart) {
    return { ok: false, error: 'A requested start date and time is required.' };
  }
  if (!deadline) return { ok: false, error: 'A hard deadline is required.' };

  // Internal sign-off (§A6): if the supervisor asked to require sign-off, a
  // recipient email is mandatory — otherwise the server would silently
  // auto-schedule (no recipient = no approval route).
  const signoffEmail = draft.signoff_recipient_email?.trim() || null;
  if (draft.require_signoff && !signoffEmail) {
    return {
      ok: false,
      error: 'Add a sign-off recipient email, or turn off "require sign-off".',
    };
  }

  // Pickup origins are a list (VCS-203) — trim, drop blanks, de-dupe; empty → null.
  const origins = [
    ...new Set((draft.origin_warehouse ?? []).map((w) => w.trim()).filter(Boolean)),
  ];

  const value: ProjectCreateInput = {
    title,
    client_name: client,
    source: draft.source ?? fallbackSource,
    kind: draft.kind ?? 'install',
    priority: draft.priority ?? 'normal',
    sap_order_number: draft.sap_order_number ?? null,
    sap_project_id: draft.sap_project_id ?? null,
    customer_number: draft.customer_number ?? null,
    design_job_number: draft.design_job_number ?? null,
    design_pd_number: draft.design_pd_number ?? null,
    parent_project_id: draft.parent_project_id ?? null,
    site_location_id: draft.site_location_id ?? null,
    site_address: draft.site_address ?? null,
    site_contact_name: draft.site_contact_name ?? null,
    site_contact_phone: draft.site_contact_phone ?? null,
    site_notes: draft.site_notes ?? null,
    // Empty / "Other but blank" normalizes to null — no phantom origin.
    origin_warehouse: origins.length ? origins : null,
    pm_user_id: draft.pm_user_id ?? null,
    interior_designer: draft.interior_designer ?? null,
    interior_designer_email: draft.interior_designer_email?.trim() || null,
    csm: draft.csm ?? null,
    csm_email: draft.csm_email?.trim() || null,
    sales_executive: draft.sales_executive ?? null,
    sales_executive_email: draft.sales_executive_email?.trim() || null,
    status_update_recipients: draft.status_update_recipients ?? null,
    delivery_install_budget_cents: draft.delivery_install_budget_cents ?? null,
    // Bid inputs (§A6b) — supervisor-only; the server computes + stores the full
    // bid from these. Ignored for a PM-created request.
    bid_labor_cents: draft.bid_labor_cents ?? null,
    // Forward the per-role labor breakdown + the saved mileage journey too —
    // without these the create path keeps only the flat totals (the role mix /
    // itinerary built on the create screen would be silently dropped).
    bid_labor_roles: draft.bid_labor_roles ?? null,
    bid_mileage_miles: draft.bid_mileage_miles ?? null,
    bid_mileage_legs: draft.bid_mileage_legs ?? null,
    bid_debris_quarters: draft.bid_debris_quarters ?? null,
    // Internal sign-off (§A6) — supervisor-only; the server ignores these for a
    // PM-created request. Blank recipient fields normalize to null/undefined.
    require_signoff: draft.require_signoff ?? undefined,
    signoff_recipient_name: draft.signoff_recipient_name?.trim() || null,
    signoff_recipient_email: signoffEmail,
    signoff_show_cost: draft.signoff_show_cost ?? undefined,
    intake_notes: draft.intake_notes ?? null,
    requested_start_date: requestedStart,
    requested_end_date: draft.requested_end_date ?? null,
    requested_schedule: draft.requested_schedule ?? null,
    requested_schedule_flexible: draft.requested_schedule_flexible ?? false,
    deadline_date: deadline,
  };
  return { ok: true, value };
}

// NOTE: PM intake no longer materializes tasks client-side. The task
// breakdown is proposed server-side from the project kind in POST /projects
// (PMs are requesters, not operators) and the supervisor edits it on the
// project screen — so the old `proposedTaskToCreateInput` mapper was removed.
